import linksJson from "./links.json" with { type: "json" };

/**
 * @tipranks/links — internal links registry (adr/0009).
 *
 * `links.json` (ported from the legacy `trLinks.json`) is the single source of
 * truth for internal routes, nav text, GTM ids, and link metadata. This module
 * adds a typed accessor + `href()` builder so a broken link is an error rather
 * than a silent bad URL.
 */

/** Shape of a single registry entry (fields are sparse across entries). */
export interface LinkEntry {
  /**
   * Route template, with `:param` slots (e.g. `/screener/trending-stocks/:marketCountry`).
   * Optional: a few entries are nav/query-only and carry no URL template.
   */
  link?: string;
  /** Human label (nav/menu text). */
  text?: string;
  /** GTM id, carried through to analytics (adr/0011). */
  gid?: string;
  /**
   * Allowed values per param. An array enumerates allowed values; a non-array
   * (e.g. `1`) marks a required free-form param (ticker, slug, …).
   */
  params?: Record<string, readonly string[] | number | string>;
  /**
   * Every value the ROUTE answers for an enumerated param, when that is more
   * than `params` lists. `params` stays what the UI links: the market pickers,
   * the menus and the sitemap read it. `servedParams` adds the URLs production
   * also answers 200 (with its own canonical) but never links — e.g.
   * `/screener/trending-stocks/japan` (TASK-0514). `href()` accepts both lists;
   * read the served list with {@link servedValues}. Absent → `params`.
   */
  servedParams?: Record<string, readonly string[]>;
  icon?: string;
  badge?: string;
  image?: string;
  /** Owning app/area (e.g. `app-research`, `external`). */
  app?: string;
  /** Anchor target, e.g. `_blank`. */
  target?: string;
  /** Query-param spec for entries routed by query rather than path. */
  query?: Record<string, unknown>;
  reportWatchCount?: boolean;
  /**
   * Legacy `trLinks` "feature" flag. It decides whether a chrome link carries
   * campaign attribution — see {@link withFeatureAttribution}.
   */
  isFeature?: boolean;
}

const registry = linksJson as Record<string, LinkEntry>;

/** Union of every valid link key — an unknown key is a compile error. */
export type LinkKey = keyof typeof linksJson;

// Matches a whole `/:param` segment, including the optional marker (`/:category?`).
// Anchoring on the slash lets an optional param drop its entire segment; every
// registry template uses `/`-anchored slots (checked in links.test.ts).
const PARAM_RE = /\/:([A-Za-z0-9_]+)(\?)?/g;

/**
 * `/stocks/:ticker/…`, `/etf/:ticker/…` and `/mutual-funds/:ticker/…` are
 * lower-case URLs: an upper-case ticker costs a 301 hop and splits internal link
 * equity (TR-26330). Lowering the slot here makes every registry-built link
 * canonical by construction; display text is the caller's own and stays upper-case.
 */
function slotSegment(entry: LinkEntry, name: string, value: string): string {
  if (name === "ticker" && /^\/(?:stocks|etf|mutual-funds)\//.test(entry.link ?? "")) {
    // Production serves an exchange-qualified ticker with a literal colon
    // (`/etf/au:vts`, `/mutual-funds/de:1kh`); `%3A` is a different path that 301s
    // there (and upper-case hex) — TR-26504.
    return encodeURIComponent(value.toLowerCase()).replace(/%3A/gi, ":");
  }
  if (name === "ticker" && /^\/commodities\//.test(entry.link ?? "")) {
    // The commodity URL is the `cm:` ticker with a literal colon
    // (`/commodities/cm:xauusd`, self-canonical on production — TR-26522).
    return encodeURIComponent(value).replace(/%3A/gi, ":");
  }
  return encodeURIComponent(value);
}

/** Params accepted by `href` — slot name → value. */
export type HrefParams = Record<string, string | number>;

function isExternalLink(link: string): boolean {
  return /^[a-z]+:\/\//i.test(link) || link.startsWith("//");
}

/** True if the link points off-site (absolute URL or protocol-relative). */
export function isExternal(key: LinkKey): boolean {
  const link = registry[key].link;
  return link ? isExternalLink(link) : false;
}

/** Full metadata entry for a key (text/icon/gid/target/…). */
export function meta(key: LinkKey): LinkEntry {
  return registry[key];
}

/**
 * The values the route answers for an enumerated param: `servedParams[name]`
 * when the entry declares one, else `params[name]`. `undefined` when the param
 * is free-form or absent. For route validation; a UI that LISTS values (a market
 * picker) reads `meta(key).params` instead.
 */
export function servedValues(key: LinkKey, name: string): readonly string[] | undefined {
  const entry = registry[key];
  const values = entry.servedParams?.[name] ?? entry.params?.[name];
  return Array.isArray(values) ? values : undefined;
}

/** GTM id for a key (for analytics, adr/0011), or `undefined`. */
export function gid(key: LinkKey): string | undefined {
  return registry[key].gid;
}

/**
 * Build the URL for a link key, filling `:param` slots and validating them.
 *
 * - Unknown `key` → compile error (and a runtime throw if called from JS).
 * - Missing a required param, or a value outside the registry's allowed set,
 *   throws — never returns a silently-wrong URL.
 * - An **optional** slot (`/:category?`) drops its whole segment when the param
 *   is missing/empty: `href("article", { slug: "s" })` → `/news/s`, and
 *   `href("article", { category: "crypto", slug: "s" })` → `/news/crypto/s`.
 *
 * @example href("trending-stocks", { marketCountry: "us" }) // "/screener/trending-stocks/us"
 */
export function href(key: LinkKey, params: HrefParams = {}): string {
  const entry = registry[key];
  if (!entry) throw new Error(`Unknown link key: "${String(key)}"`);
  if (!entry.link) throw new Error(`Link "${String(key)}" has no URL template`);

  return absolutePath(fillParams(key, entry, params));
}

/**
 * `href()` with LEGACY's lenient param semantics - for surfaces that port a
 * legacy config verbatim (the mobile drawer).
 *
 * Legacy `getTrLinkProps` -> `setParams` (tr-common `formatter/url.ts`) fills
 * every `:slot` of the template from `params` and substitutes `""` for a missing
 * or empty value; it never throws and never checks the allowed set. Then
 * `removeMultiSlash` and `removeTrailingSlash` run. So a config entry with
 * `marketCountry: ""` (or no params at all) drops the segment:
 * `/screener/stocks/:marketCountry?` -> `/screener/stocks`, and
 * `/compare-stocks/:marketCountry` -> `/compare-stocks`. That is how production
 * serves the US market: a US link has NO market segment.
 *
 * Strict `href()` stays the default - it fails loud on a missing required param.
 * Use this one only where the legacy config is the contract.
 */
export function legacyHref(key: LinkKey, params: HrefParams = {}): string {
  const entry = registry[key];
  if (!entry) throw new Error(`Unknown link key: "${String(key)}"`);
  if (!entry.link) throw new Error(`Link "${String(key)}" has no URL template`);

  const filled = entry.link.replace(PARAM_RE, (_match, name: string) => {
    const raw = params[name];
    return raw === undefined || raw === null || raw === "" ? "" : `/${slotSegment(entry, name, String(raw))}`;
  });
  // Collapse repeated slashes outside the scheme (`https://` / `//`), then drop a trailing slash.
  const scheme = /^([a-z][a-z0-9+.-]*:)?\/\//i.exec(filled)?.[0] ?? "";
  const rest = filled.slice(scheme.length).replace(/\/{2,}/g, "/");
  const joined = `${scheme}${rest}`;
  const trimmed = joined.length > 1 ? joined.replace(/\/+$/, "") : joined;
  return absolutePath(trimmed);
}

/**
 * Root-relative, always.
 *
 * TEN registry entries carry a template with NO leading slash — `sign-up`,
 * `chat`, `etfs`, `stocks/:ticker/drug-pipeline`, … — copied verbatim from
 * legacy's `trLinks.json`, where the router normalized them. Ours did not, so
 * `href("sign-up")` returned `"sign-up"`: a RELATIVE url that resolves against
 * the current page. Found 2026-09-01 on `/stocks/aapl/forecast`, where the
 * sign-up popup's CTA pointed at `/stocks/aapl/sign-up`.
 *
 * Normalizing here rather than editing the JSON keeps the registry a faithful
 * copy of legacy's (its diffability is the point) and fixes all ten at once —
 * including the two popups that were already shipping the broken href.
 * Protocol-absolute, protocol-relative, hash and mailto targets pass through.
 */
function absolutePath(link: string): string {
  if (/^([a-z][a-z0-9+.-]*:|\/\/|\/|#)/i.test(link)) return finalUrl(link);
  return finalUrl(`/${link}`);
}

/**
 * Registry destinations that answer with a redirect or a canonical elsewhere.
 * A header, footer or drawer link to one of them costs a crawl hop and sends
 * internal link equity through the redirect (TR-26328). `href()` and
 * `legacyHref()` return the FINAL url instead, so every menu gets it at once.
 * The registry keeps legacy's value (its diffability is the point); this map is
 * the one place the final urls live. Keys are exact: a deeper path or a link
 * with a query string is never rewritten.
 *
 * - `/faq` is a 301 to the glossary term.
 * - `/calendars/stock-splits` canonicalises to `/calendars/stock-splits/upcoming`.
 * - `/experts/analysts/top` is a 301 to `/experts/analysts`.
 * - The `lp.tipranks.com` pages 301 to the same path with a trailing slash;
 *   `legacyHref()` strips that slash, so it is restored here.
 */
const FINAL_URLS: Readonly<Record<string, string>> = {
  "/faq": "/glossary/f/faq",
  "/calendars/stock-splits": "/calendars/stock-splits/upcoming",
  "/experts/analysts/top": "/experts/analysts",
  "https://lp.tipranks.com/how-to-use": "https://lp.tipranks.com/how-to-use/",
  "https://lp.tipranks.com/careers": "https://lp.tipranks.com/careers/",
  "https://lp.tipranks.com/advertise-with-us": "https://lp.tipranks.com/advertise-with-us/",
  "https://lp.tipranks.com/mobile-app": "https://lp.tipranks.com/mobile-app/",
};

/** The final url for a resolved registry link (see {@link FINAL_URLS}). */
export function finalUrl(link: string): string {
  return FINAL_URLS[link] ?? link;
}

function fillParams(key: LinkKey, entry: LinkEntry, params: HrefParams): string {
  return (entry.link as string).replace(PARAM_RE, (_match, name: string, optional?: string) => {
    const raw = params[name];
    if (raw === undefined || raw === null || raw === "") {
      if (optional) return ""; // optional segment: omit it entirely
      throw new Error(`Missing param "${name}" for link "${String(key)}"`);
    }
    const value = String(raw);
    // The registry keys optional params with the marker (e.g. `"category?"`).
    // A served-but-unlinked value is a real URL too (`servedParams`).
    const allowed =
      entry.servedParams?.[name] ?? entry.params?.[name] ?? entry.params?.[`${name}?`];
    if (Array.isArray(allowed) && !allowed.includes(value)) {
      throw new Error(
        `Invalid value "${value}" for param "${name}" of link "${String(key)}". ` +
          `Allowed: ${allowed.join(", ")}`
      );
    }
    return `/${slotSegment(entry, name, value)}`;
  });
}

/**
 * Serialize a registry entry's `query` metadata to a query string (leading
 * `?`), or `""` when the entry carries none. Campaign params (`llf`,
 * `custom18`, `utm_*`) live in the registry (adr/0009), not hard-coded in
 * components. Values are stringified; nullish values are skipped.
 */
export function linkQuery(key: LinkKey): string {
  const query = registry[key].query;
  if (!query) return "";
  const usp = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    if (value !== undefined && value !== null) usp.set(name, String(value));
  }
  const serialized = usp.toString();
  return serialized ? `?${serialized}` : "";
}

/**
 * `href()` with the entry's registry `query` metadata appended — e.g.
 * `hrefWithQuery("header-go-pro")` →
 * `https://lp.tipranks.com/go-pro-v2?custom18=home&llf=header-go`.
 */
export function hrefWithQuery(key: LinkKey, params: HrefParams = {}): string {
  const query = registry[key].query;
  return query
    ? withQuery(href(key, params), query as Record<string, string | number | boolean | null | undefined>)
    : href(key, params);
}

/**
 * Append query params to an href that MAY already carry a query string —
 * `?` when it has none, `&` when it does; nullish values are skipped.
 *
 * Why this exists: legacy repointed `go-premium`/`go-ultimate` from the LP
 * hosts to `/checkout/v2?subscription=…` (TR-26213, 2026-09), so every caller
 * that used to write `` `${href("go-premium")}?llf=…` `` would now produce
 * `…yearly?llf=…` — a second `?` that the checkout page reads as part of the
 * `subscription` value. Legacy's `TrLink` merges `extend.query` into the URL
 * object, so it never had this problem; this is the same merge for ours.
 */
export function withQuery(
  destination: string,
  params: Record<string, string | number | boolean | null | undefined>
): string {
  const usp = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) usp.set(name, String(value));
  }
  const serialized = usp.toString();
  if (!serialized) return destination;
  // Keep a fragment (if any) after the query.
  const hashAt = destination.indexOf("#");
  const base = hashAt === -1 ? destination : destination.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : destination.slice(hashAt);
  const joiner = base.includes("?") ? (base.endsWith("?") || base.endsWith("&") ? "" : "&") : "?";
  return `${base}${joiner}${serialized}${hash}`;
}

/**
 * Legacy `TrLink putPageLlfInQuery="on-feature"` — the attribution the footer
 * and the mobile drawer put on their links: a link whose registry entry is
 * `isFeature` gets `llf` plus `custom18=<pageName>`; every other link stays
 * bare. (`TrLink` adds `custom18` only once an `llf` is set, and only when a
 * page name exists.) On the live registry that is `plans` in the footer, and
 * `plans` + the newsletter LPs in the drawer — production's
 * `/plans?custom18=homeV2&llf=footer-plans` and
 * `/plans?llf=mobile-menu-right-plans-link&custom18=homeV2` (2026-10-04).
 *
 * `order` exists because the two surfaces serialize differently: the footer
 * seeds `plans`' query with `custom18` BEFORE `TrLink` adds `llf`
 * (`FooterTipranks.tsx`), so its params read page-first; the drawer's read
 * llf-first.
 */
export function withFeatureAttribution(
  key: LinkKey,
  destination: string,
  { llf, pageName, order = "llf-first" }: { llf: string; pageName?: string; order?: "llf-first" | "page-first" }
): string {
  if (!registry[key].isFeature) return destination;
  const custom18 = pageName || undefined;
  return withQuery(destination, order === "page-first" ? { custom18, llf } : { llf, custom18 });
}

/**
 * Google interstitial (vignette) opt-out — legacy `getTrLinkProps().disableAds`
 * (`routing.ts`), rendered by `TrLink` as `data-google-interstitial="false"`.
 * A navigation into checkout, sign-in/up or a landing page must not be
 * interrupted by a Google vignette ad, so the link carries the opt-out.
 *
 * The rule, verbatim from legacy, on a registry entry: `gid` is `payment` or
 * `auth`, OR the (resolved) link contains `lp.tipranks.com`.
 */
const OPT_OUT_GIDS: ReadonlySet<string | undefined> = new Set(["payment", "auth"]);
const LP_HOST = "lp.tipranks.com";

/** The attribute name Google's interstitial script reads off the clicked anchor. */
export const INTERSTITIAL_ATTR = "data-google-interstitial";

/** Does the registry entry opt its links out of Google interstitials? */
export function disablesAds(key: LinkKey): boolean {
  const entry = registry[key];
  return OPT_OUT_GIDS.has(entry.gid) || (entry.link?.includes(LP_HOST) ?? false);
}

const SITE_ORIGIN = "https://www.tipranks.com";

/** Opt-out destinations, derived once from the registry entries that carry the rule. */
const optOutMatchers = (() => {
  const paths: RegExp[] = [];
  const absolute = new Set<string>();
  for (const key of Object.keys(registry) as LinkKey[]) {
    const link = registry[key].link;
    if (!link || !disablesAds(key) || link.includes(LP_HOST)) continue;
    const url = new URL(link, SITE_ORIGIN);
    const pathname = trimSlash(url.pathname);
    if (isExternalLink(link)) {
      absolute.add(`${url.origin}${pathname}`);
      continue;
    }
    const source = pathname
      .split("/")
      .map((segment) =>
        segment.startsWith(":")
          ? segment.endsWith("?")
            ? "(?:/[^/]+)?"
            : "/[^/]+"
          : segment
            ? `/${segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`
            : ""
      )
      .join("");
    paths.push(new RegExp(`^${source}$`, "i"));
  }
  return { paths, absolute };
})();

function trimSlash(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

/**
 * Same rule, for a call site that holds only the finished href (the common
 * case — `href()`/`hrefWithQuery()`/`withQuery()` output, with or without
 * campaign params). True when the href is an `lp.tipranks.com` URL, a path of
 * an opt-out registry entry (`/sign-in`, `/plans`, `/checkout/v2?…`), or an
 * absolute opt-out URL (the app-download redirect). Query and hash are ignored.
 */
export function hrefDisablesAds(destination: string | undefined | null): boolean {
  if (!destination) return false;
  if (destination.includes(LP_HOST)) return true;
  let url: URL;
  try {
    url = new URL(destination, SITE_ORIGIN);
  } catch {
    return false;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  const pathname = trimSlash(url.pathname);
  if (/(^|\.)tipranks\.com$/.test(url.hostname) && url.hostname !== "click.tipranks.com") {
    return optOutMatchers.paths.some((re) => re.test(pathname));
  }
  return optOutMatchers.absolute.has(`${url.origin}${pathname}`);
}

/**
 * Spread onto an anchor/button that renders `destination`:
 * `<a href={x} {...interstitialProps(x)}>`. Empty when the link keeps ads.
 */
export function interstitialProps(destination: string | undefined | null): {
  "data-google-interstitial"?: "false";
} {
  return hrefDisablesAds(destination) ? { [INTERSTITIAL_ATTR]: "false" } : {};
}

/** All registry keys (e.g. for nav iteration / consistency checks). */
export const linkKeys = Object.keys(registry) as LinkKey[];

export { registry as links };

// Header mega-menu tree (TASK-0245) — top-level categories → grouped columns →
// items, all single-sourced to registry keys.
export * from "./menu.js";

// Mobile drawer menu tree (production-parity) — quick-links + accordion
// categories + popular-stocks, all single-sourced to registry keys.
export * from "./mobile-menu.js";
